import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { BarChartDataItemProps } from "./ChartTypes";

interface DonutChartProps {
  parentid?: string;
  data: BarChartDataItemProps[];
  sort?: boolean;
  handleClick?: (label: string, e: any) => void;
  clickReturn?: string;
  labelPositon?: string;
  customLabel?: string;
}

const VerticalBarChart: React.FC<DonutChartProps> = ({
  parentid,
  data,
  sort = true,
  handleClick = () => {},
  clickReturn = "label",
  labelPositon = "bottom",
  customLabel = "",
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [labelPos, setLabelPos] = React.useState<string>(labelPositon);

  const margin = {
    top: 10,
    right: 10,
    bottom: labelPos == "bottom" ? 40 : 10,
    left: 10,
  };

  useEffect(() => {
    if (!svgRef.current) return;
    d3.select(svgRef.current).selectAll("*").remove();

    const parentElement = parentid
      ? document.getElementById(parentid)
      : parentRef.current;
    if (!parentElement) {
      // Handle the error
      console.error(`No element with ID ${parentid}`);
      return;
    }

    if (sort) {
      data.sort((a, b) => b.value - a.value);
    }

    const rect = parentElement.getBoundingClientRect();
    const width = rect.width - margin.left - margin.right;
    const height = rect.height - margin.top - margin.bottom;
    const svg = d3
      .select(svgRef.current)
      .attr("width", rect.width)
      .attr("height", rect.height)
      .append("g")
      .attr("transform", "translate(" + 0 + "," + margin.top + ")");

    if (data.length > 10) {
      data = data.slice(0, 10);
    }

    const perItemWidth = width / data.length;
    const maxLabelLength =
      data.reduce((acc, current) => {
        return Math.max(acc, current.label.length);
      }, 0) * 8;
    const maxChar = Math.round(perItemWidth / 10);

    const max = data.reduce((acc, current) => {
      return Math.max(acc, current.value);
    }, -Infinity);

    // X axis
    const x = d3
      .scaleBand()
      .range([0, width])
      .domain(
        data.map(function (d) {
          return d.label;
        }),
      )
      .padding(0.7);

    if (labelPos === "bottom") {
      svg
        .append("g")
        .attr("transform", "translate(0," + height + ")")
        .call(d3.axisBottom(x))
        .selectAll("text")
        .style("text-anchor", "center")
        .style("cursor", "pointer")
        .style("fill", "var(--foreground)")
        .style("font-size", "0.7rem")
        .on("click", function (d: any, i: any) {
          d.preventDefault();
          if (handleClick) {
            if (clickReturn === "object") {
              const dt = data.find((d) => d.label === i);
              handleClick(JSON.stringify(dt), d);
            } else {
              handleClick(i, d);
            }
          }
        });
    }

    function formatNumber(num: number) {
      if (num > 1000000) {
        return (num / 1000000).toFixed(2) + "M";
      } else if (num > 1000) {
        return (num / 1000).toFixed(2) + "K";
      } else {
        return num;
      }
    }

    // Add Y axis
    const y = d3
      .scaleLinear()
      .domain([0, max])
      .range([height - margin.top, 0]);
    // svg.append("g")
    // .attr("transform", "translate(0," + margin.top + ")")
    // .call(d3.axisLeft(y)).selectAll("text").style("fill", "white").text((d) => formatNumber(d));

    svg.selectAll("path").style("stroke", "none");
    svg.selectAll(".tick>line").style("stroke", "none");

    const bargroups = svg
      .selectAll("gs")
      .data(data)
      .enter()
      .append("g")
      .attr("class", "barGroup")
      .attr("data-label", (d: any) => d.label);
    // Bars
    bargroups
      .append("rect")
      .attr("x", function (d) {
        return String(x(d.label));
      })
      .attr("y", height - margin.top)
      .attr("width", x.bandwidth())
      .attr("height", 0)
      .attr("rx", 2)
      .attr("fill", "#57BAF1")
      .transition()
      .duration(1000)
      .delay((_d, i) => i * 100)
      .attr("height", function (d) {
        let newHeight = height - y(d.value) - margin.top - 10;
        if (newHeight < 0) {
          newHeight = 0;
        }
        return newHeight;
      })
      .attr("y", function (d) {
        return y(d.value) + margin.top + 10;
      })
      .style("cursor", "pointer");

    const verticalGroup = bargroups
      .append("g")
      .attr("class", "verticalGroup")
      .attr("data-v", (d: any) => d.label)
      .style(
        "transform-origin",
        (d: any) => `${(x(d.label) ?? 0) - 5}px ${height}px`,
      )
      .attr("transform", `rotate(-90)`)
      .style("fill", "var(--foreground)")
      .style("font-size", "0.7rem");

    if (labelPos === "bar") {
      bargroups
        .append("rect")
        .attr("x", function (d: any) {
          return String((x(d.label) || 0) - 18);
        })
        .attr("y", 0)
        .attr("width", 50)
        .attr("height", height)
        .attr("rx", 2)
        .attr("fill", "#00000000");

      verticalGroup
        .append("rect")
        .attr("x", function (d: any) {
          return String((x(d.label) || 0) - 10);
        })
        .attr("y", height - 14)
        .attr("width", 0)
        .attr("height", 20)
        .attr("rx", 2)
        .attr("class", "barBG")
        .style("pointer-events", "none")
        .attr("stroke", "var(--map-info-stroke)")
        .attr("fill", "var(--map-info-background)");

      verticalGroup
        .append("text")
        .attr("class", "barName")
        .style("pointer-events", "none")
        .attr("data-name", (d: any) => d.label)
        .attr("x", function (d: any) {
          return String((x(d.label) ?? 0) - 5);
        })
        .attr("y", function () {
          return height;
        })
        .style("fill", "var(--foreground)")
        .text((d: any) => {
          let labelText = d.label;
          if (labelText.length > height / 6) {
            labelText = labelText.substring(0, height / 6) + "...";
          }
          return labelText;
        })
        .attr("data-shortName", (d: any) => {
          let labelText = d.label;
          if (labelText.length > height / 6) {
            labelText = labelText.substring(0, height / 6) + "...";
          }
          return labelText;
        });
    }

    bargroups
      .append("text")
      .attr("x", function (d: any) {
        return String((x(d.label) ?? 0) + x.bandwidth() / 2);
      })
      .attr("y", margin.top)
      .attr("class", "value")
      .attr("text-anchor", "middle")
      .style("cursor", "pointer")
      .style("pointer-events", "none")
      .text((d) => formatNumber(d.value))
      .style("fill", "var(--foreground)")
      .style("font-size", "0.7rem");

    bargroups
      .on("mouseenter", function (_, i) {
        const text = d3.select(this).select("text.value");
        text
          .transition()
          .duration(500)
          .attr("y", Math.max(y(i.value) + margin.top));

        d3.select(this).raise();

        if (d3.select(this).selectAll(".barBG").nodes().length > 0) {
          d3.select(this)
            .select(".verticalGroup")
            .transition()
            .duration(500)
            .attr("transform", `rotate(0) scale(1.2)`);

          const barName = d3.select(this).select(".barName");

          const originalName = barName.attr("data-name");
          barName
            .style("font-weight", "bold")
            .style("fill", "black")
            .text(originalName);

          let textLength = 0;
          const reference: any = d3.select(this);
          if (reference) {
            textLength = reference.select(".barName").node()?.getBBox().width;
          }

          d3.select(this)
            .select(".barBG")
            .transition()
            .duration(500)
            .attr("width", textLength + 12);

          svg
            .selectAll(`[data-label]:not([data-label="${i.label}"])`)
            .transition()
            .duration(500)
            .style("opacity", 0.2);
        }
      })
      .on("mouseout", function () {
        const text = d3.select(this).select("text.value");
        text.transition().duration(500).attr("y", margin.top);

        const tooltip = tooltipRef.current;
        if (tooltip) {
          tooltip.style.opacity = "0";
        }

        if (d3.select(this).selectAll(".barBG").nodes().length > 0) {
          d3.select(this)
            .select(".verticalGroup")
            .transition()
            .duration(500)
            .style("font-size", "0.7rem")
            .attr("transform", `rotate(-90)`);

          d3.select(this)
            .select(".barBG")
            .transition()
            .duration(500)
            .attr("width", 0);

          const barName = d3.select(this).select(".barName");

          const shortName = barName.attr("data-shortName");
          barName
            .style("font-weight", "normal")
            .style("fill", "var(--foreground)")
            .text(shortName);

          svg
            .selectAll(".barGroup")
            .transition()
            .duration(500)
            .style("opacity", 1);
        }
      })
      .on("mousemove", function (d, i) {
        const tooltip = tooltipRef.current;
        if (tooltip) {
          const name = tooltip.querySelector(".name");
          const value = tooltip.querySelector(".value");
          if (name) {
            name.innerHTML = i.label;
          }
          if (value) {
            value.innerHTML = formatNumber(i.value) + "";
          }

          tooltip.style.opacity = "1";
          tooltip.style.left = d.pageX - tooltip.offsetWidth - 5 + "px";
          tooltip.style.top = d.pageY - tooltip.offsetHeight - 5 + "px";
        }
      })
      .on("click", function (d: any, i: any) {
        d.preventDefault();
        if (handleClick) {
          if (clickReturn === "object") {
            const dt = data.find((d) => d.label == i.label);
            handleClick(JSON.stringify(dt), d);
          } else {
            handleClick(i.label, d);
          }
        }
      });

    svg
      .selectAll(".tick")
      .selectAll("text")
      .text(function (d: any) {
        let newText = d;
        if (d.length > maxChar) {
          newText = d.substring(0, maxChar) + "...";
        }
        return newText;
      });

    if (maxLabelLength > perItemWidth) {
      setLabelPos("bar");
    }
  }, [data, labelPos]);

  return (
    <div className="relative overflow-visible h-full w-full" ref={parentRef}>
      <svg ref={svgRef} className="overflow-visible"></svg>
      <div
        ref={tooltipRef}
        className={
          "fixed top-80 left-80 text-xs text-primary-background rounded-md pointer-events-none bg-foreground p-2 transition-all duration-500 ease-in-out " +
          (labelPos == "bar" ? "hidden" : "")
        }
        style={{ opacity: 0 }}
      >
        <div className="name">- - -</div>
        <div className="mt-1">
          <span className="valueLabel">
            {customLabel != "" ? customLabel : "Count"} :{" "}
          </span>
          <span className="value">0</span>
        </div>
      </div>
    </div>
  );
};

const MemoizedVerticalBarChart = React.memo(VerticalBarChart);
export default MemoizedVerticalBarChart;
